import { useRef, useState, useEffect } from 'react';

interface TrendingPrompt {
  id: number;
  text: string;
  tag: string;
  tagColor: string;
  icon: string;
}

const TRENDING_PROMPTS: TrendingPrompt[] = [
  { id: 1, text: 'A futuristic cyberpunk city at night, neon rain, flying cars weaving through holographic ads, bokeh depth of field', tag: 'Trending', tagColor: 'from-red-500 to-orange-500', icon: 'ri-fire-fill' },
  { id: 2, text: 'Slow-motion ocean wave crashing on volcanic black sand beach at golden hour, mist rising, seabirds in flight', tag: 'Hot', tagColor: 'from-orange-500 to-amber-500', icon: 'ri-flashlight-fill' },
  { id: 3, text: 'Astronaut floating in deep space, Earth reflecting in visor, soft score music, dramatic silence, infinite stars', tag: 'Viral', tagColor: 'from-pink-500 to-rose-500', icon: 'ri-bard-fill' },
  { id: 4, text: 'Ancient forest path bathed in god rays, ethereal mist, wildlife stirring at dawn, cinematic panning shot', tag: 'Popular', tagColor: 'from-green-500 to-teal-500', icon: 'ri-heart-3-fill' },
  { id: 5, text: 'Luxury product close-up: a diamond watch face with gears turning in macro, glittering reflections, dark backdrop', tag: 'Top Pick', tagColor: 'from-purple-500 to-indigo-400', icon: 'ri-award-fill' },
  { id: 6, text: 'Time-lapse of a bustling Tokyo street intersection from dusk to midnight, kaleidoscope of light trails', tag: 'Trending', tagColor: 'from-red-500 to-orange-500', icon: 'ri-fire-fill' },
  { id: 7, text: 'Renaissance portrait style AI-generated face morphing slowly, rich oil-paint texture, candlelit warm tones', tag: 'New', tagColor: 'from-sky-500 to-cyan-500', icon: 'ri-sparkling-fill' },
  { id: 8, text: 'Drone shot descending into a lush rainforest canopy, birds scattering, waterfall visible in distance', tag: 'Hot', tagColor: 'from-orange-500 to-amber-500', icon: 'ri-flashlight-fill' },
  { id: 9, text: 'Ballet dancer in slow motion against a stark white studio, feathers falling, ethereal and graceful motion', tag: 'Viral', tagColor: 'from-pink-500 to-rose-500', icon: 'ri-bard-fill' },
  { id: 10, text: 'Vintage 1980s VHS-style skateboarding montage, lo-fi grain, sunset suburbia, nostalgic synth soundtrack', tag: 'Retro', tagColor: 'from-yellow-500 to-orange-400', icon: 'ri-history-fill' },
  { id: 11, text: 'Underwater coral reef ecosystem, bioluminescent creatures emerging at night, ultra-clear 8K ocean footage', tag: 'Top Pick', tagColor: 'from-purple-500 to-indigo-400', icon: 'ri-award-fill' },
  { id: 12, text: 'Race car cockpit POV at 200mph, helmet visor reflecting burning asphalt, crowd blur, adrenaline pulse', tag: 'Popular', tagColor: 'from-green-500 to-teal-500', icon: 'ri-heart-3-fill' },
  { id: 13, text: 'Architect&apos;s vision: modern glass house on clifftop, storm approaching over ocean, structural silhouette', tag: 'New', tagColor: 'from-sky-500 to-cyan-500', icon: 'ri-sparkling-fill' },
  { id: 14, text: 'Miniature world food scene: tiny chefs preparing ramen, steam rising, macro lens magic, warm kitchen glow', tag: 'Trending', tagColor: 'from-red-500 to-orange-500', icon: 'ri-fire-fill' },
  { id: 15, text: 'Fashion editorial: model walking through sandstorm in haute couture, slow motion fabric and hair movement', tag: 'Hot', tagColor: 'from-orange-500 to-amber-500', icon: 'ri-flashlight-fill' },
];

interface TrendingPromptsTickerProps {
  onSelectPrompt: (prompt: string) => void;
  darkMode: boolean;
}

export default function TrendingPromptsTicker({ onSelectPrompt, darkMode }: TrendingPromptsTickerProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const animRef = useRef<number | null>(null);
  const posRef = useRef(0);
  const SPEED = 0.6;

  // duplicate the list for seamless loop
  const doubled = [...TRENDING_PROMPTS, ...TRENDING_PROMPTS];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const halfWidth = track.scrollWidth / 2;

    const step = () => {
      if (!isPaused) {
        posRef.current += SPEED;
        if (posRef.current >= halfWidth) posRef.current = 0;
        track.style.transform = `translateX(-${posRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(step);
    };

    animRef.current = requestAnimationFrame(step);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [isPaused]);

  const handleClick = (item: TrendingPrompt) => {
    setActiveId(item.id);
    onSelectPrompt(item.text.replace(/&apos;/g, "'"));
    setTimeout(() => setActiveId(null), 1000);
  };

  return (
    <div className={`relative w-full overflow-hidden rounded-2xl border ${
      darkMode
        ? 'bg-gray-800/70 border-gray-700'
        : 'bg-gradient-to-r from-orange-50 via-red-50 to-orange-50 border-orange-200'
    }`}
    >
      {/* Header strip */}
      <div className={`flex items-center gap-3 px-5 py-3 border-b ${
        darkMode ? 'border-gray-700' : 'border-orange-200'
      }`}>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="relative w-5 h-5 flex items-center justify-center">
            <i className="ri-fire-fill text-red-500 text-base animate-pulse"></i>
          </div>
          <span className={`text-xs font-bold uppercase tracking-wider ${
            darkMode ? 'text-orange-400' : 'text-orange-600'
          }`}>Trending Prompts</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
            darkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-600'
          }`}>Live</span>
        </div>
        <div className={`flex-1 text-[11px] hidden sm:block ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Click any prompt to instantly load it
        </div>
        <button
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onClick={() => setIsPaused(p => !p)}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all flex-shrink-0 cursor-pointer ${
            isPaused
              ? darkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-600'
              : darkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-500 hover:bg-gray-100'
          }`}
        >
          <i className={`${isPaused ? 'ri-play-fill' : 'ri-pause-fill'} text-xs`}></i>
          {isPaused ? 'Resume' : 'Pause'}
        </button>
      </div>

      {/* Scrolling track */}
      <div
        className="relative py-3 overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* fade edges */}
        <div className={`absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none ${
          darkMode
            ? 'bg-gradient-to-r from-gray-800/70 to-transparent'
            : 'bg-gradient-to-r from-orange-50 to-transparent'
        }`} />
        <div className={`absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none ${
          darkMode
            ? 'bg-gradient-to-l from-gray-800/70 to-transparent'
            : 'bg-gradient-to-l from-orange-50 to-transparent'
        }`} />

        <div ref={trackRef} className="flex gap-2.5 w-max px-4">
          {doubled.map((item, idx) => (
            <button
              key={`${item.id}-${idx}`}
              onClick={() => handleClick(item)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs font-medium transition-all flex-shrink-0 cursor-pointer whitespace-nowrap group ${
                activeId === item.id
                  ? 'border-orange-400 bg-gradient-to-r from-orange-500 to-red-500 text-white scale-95'
                  : darkMode
                  ? 'bg-gray-700/80 border-gray-600 text-gray-300 hover:border-orange-500 hover:bg-gray-700 hover:text-orange-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700'
              }`}
            >
              {/* tag badge */}
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white bg-gradient-to-r ${item.tagColor} flex-shrink-0`}>
                <i className={`${item.icon} text-[9px]`}></i>
                {item.tag}
              </span>
              <span className="max-w-[220px] truncate">
                {item.text.replace(/&apos;/g, "'")}
              </span>
              <i className={`ri-arrow-right-up-line text-xs opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${
                activeId === item.id ? 'opacity-100 text-white' : darkMode ? 'text-orange-400' : 'text-orange-500'
              }`}></i>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
